import React from 'react';
import {Cell} from '../Cell/Cell';
import {Navigation} from '../../Navigation/Navigation';
import classNames from 'classnames';
import {ProductGalleryLayout} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayout';
import {ProductDescription} from '../../ProductDescription/ProductDescription';
import {InfoSectionLayout} from '../../InfoSectionLayout/InfoSectionLayout';
import {ProductOptions} from '../../ProductOptions/ProductOptions';
import {ProductSocial} from '../../ProductSocial/ProductSocial';
import {ProductName} from '../../ProductName/ProductName';
import {ProductSku} from '../../ProductSku/ProductSku';
import s from './ClassicLayout.scss';
import {Alignment, Layout, ProductPageSlotIds} from '../../../constants';
import {LayoutComponentProps} from '../../../types/app-types';
import {createLayoutConfigWithDefaults} from '../../ProductGallery/ProductGalleryLayout/ProductGalleryLayoutUtils';
import {DetailsPlaceholder} from '../../Placeholders/DetailsPlaceholder';
import {BottomPlaceholder} from '../../Placeholders/BottomPlaceholder';
import {ProductPageButtonsContainer} from '../../ProductPageButtonsContainer/ProductPageButtonsContainer';
import {ProductPrice} from '../../ProductPrice/ProductPrice';
import {ProductPaymentBreakdown} from '../../ProductPaymentBreakdown/ProductPaymentBreakdown';
import {ProductDiscountName} from '../../ProductDiscountName/ProductDiscountName';
import {RatingSummarySlot} from '../../RatingSummarySlot/RatingSummarySlot';
import {ReviewsSlot} from '../../ReviewsSlot/ReviewsSlot';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ProductPageTopSlot} from '../../ProductPageTopSlot/ProductPageTopSlot';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {SPECS} from '../../../specs';

export const ClassicLayout: React.FunctionComponent<LayoutComponentProps> = ({settings, globals}) => {
  const GALLERY_WIDTH = 500;
  const {product, hasDiscountName, hasPaymentOptionsBreakdown, useExperiments} = globals;
  const productPageDescriptionToggle = useExperiments.enabled(SPECS.ProductPageDescriptionToggle);
  const {renderProductPageSlots} = globals.experiments ?? {};
  const isPluginInstalledInDetails1Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails1];
  const isPluginInstalledInDetails2Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails2];
  const isPluginInstalledInDetails3Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails3];
  const isPluginInstalledInDetails4Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails4];
  const isPluginInstalledInDetails8Slot = !!globals.slots?.[ProductPageSlotIds.ProductPageDetails8];
  const isPluginInstalledInBottomSlot = !!globals.slots?.[ProductPageSlotIds.ProductPageBottom];

  return (
    <article className={s.container}>
      <ProductPageTopSlot />
      <ConditionalRender by="productPage_navigation">
        <Navigation className={s.navigation} />
      </ConditionalRender>
      <div className={s.row}>
        <section className={s.col}>
          <Cell
            className={classNames(s.gallery, {
              [s.enabledNavigationPerBreakpoint]: useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint),
            })}>
            <ProductGalleryLayout
              product={product}
              layoutConfig={createLayoutConfigWithDefaults({
                withMediaBorder: true,
                withImageRatio: true,
                align: Alignment.LEFT,
                dimensions: {
                  mainMedia: {
                    widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                  },
                  thumbnails: {
                    widthConf: {num: GALLERY_WIDTH, unit: 'px'},
                    heightConf: {num: 50, unit: 'px'},
                  },
                },
              })}
              layout={Layout.Classic}
              {...settings}
            />
          </Cell>

          {productPageDescriptionToggle ? (
            <ConditionalRender by="productPage_productDescription">
              <Cell className={s.description}>
                <ProductDescription description={product.description} />
              </Cell>
            </ConditionalRender>
          ) : (
            <Cell className={s.description}>
              <ProductDescription description={product.description} />
            </Cell>
          )}
          {renderProductPageSlots && (
            <Cell
              className={classNames({
                [s.productPageSlot]: isPluginInstalledInDetails4Slot,
                [s.mediumSpacing]: isPluginInstalledInDetails4Slot,
              })}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails4} />
            </Cell>
          )}
        </section>
        <section className={s.col}>
          <Cell>
            <ProductName name={product.name} />
          </Cell>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails1Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails1} />
            </Cell>
          )}
          <RatingSummarySlot />
          <ConditionalRender by="productPage_productSku">
            <Cell className={s.sku}>
              <ProductSku />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails2Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails2} />
            </Cell>
          )}
          <Cell>
            <DetailsPlaceholder />
          </Cell>
          <ConditionalRender by="productPage_productPrice">
            <Cell className={s.price}>
              <ProductPrice />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInDetails3Slot})}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails3} />
            </Cell>
          )}

          {hasDiscountName && (
            <ConditionalRender by="productPage_showDiscountName">
              <Cell>
                <ProductDiscountName name={product.itemDiscount.discountRuleName} />
              </Cell>
            </ConditionalRender>
          )}

          {hasPaymentOptionsBreakdown && (
            <ConditionalRender by="productPage_paymentOptionsBreakdown">
              <Cell>
                <ProductPaymentBreakdown />
              </Cell>
            </ConditionalRender>
          )}

          <Cell className={s.optionsAndButtonsContainer}>
            <ProductOptions className={s.options} />
            <ProductPageButtonsContainer />
          </Cell>
          {renderProductPageSlots && (
            <Cell
              className={classNames({
                [s.productPageSlot]: isPluginInstalledInDetails8Slot,
                [s.bigSpacing]: isPluginInstalledInDetails8Slot,
              })}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails8} />
            </Cell>
          )}
          <ConditionalRender by="productPage_productInfoSection">
            <Cell className={s.info}>
              <InfoSectionLayout />
            </Cell>
          </ConditionalRender>
          {renderProductPageSlots && (
            <Cell>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageDetails9} />
            </Cell>
          )}
          <ConditionalRender by="productPage_socialNetworks">
            <Cell className={s.social}>
              <ProductSocial />
            </Cell>
          </ConditionalRender>
        </section>
      </div>
      {renderProductPageSlots && (
        <Cell className={classNames({[s.productPageSlot]: isPluginInstalledInBottomSlot})}>
          <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageBottom} />
        </Cell>
      )}
      <Cell>
        <ReviewsSlot />
        <BottomPlaceholder />
      </Cell>
    </article>
  );
};
